import type { Competitor } from '../types'

// Sources (checked 2026-10-01): lnk.bio/#pricing, lnk.bio/all-features,
// lnk.bio/custom-domain, /shop, /booking-calendar, /newsletter, Lnk.Bio blog
// posts on Page Music and video wallpapers, help.lnk.bio articles.
const lnkbio: Competitor = {
  slug: 'lnkbio',
  aliases: ['lnk-bio'],
  name: 'Lnk.Bio',
  tagline: 'Long-running link in bio with one-time plans and selling tools.',
  site: 'https://lnk.bio',
  pricingUrl: 'https://lnk.bio/all-features',
  checkedOn: '2026-10-01',
  seo: {
    title: 'Lnk.Bio alternative: mychar.gg vs Lnk.Bio compared (2026)',
    description: 'A Lnk.Bio alternative with deeper design. Compare effects, custom cursors, a full-length music player, video clips, and one-time pricing with Lnk.Bio plans.'
  },
  hero: {
    title: 'The Lnk.Bio alternative that lets you design every detail',
    description: 'Both sell one-time upgrades. mychar.gg puts the money into design: a live editor, animated effects, custom cursors, a music player, and clips that play on the page.'
  },
  summary: 'mychar.gg and Lnk.Bio both sell one-time upgrades, so the choice comes down to what the page is for. mychar.gg is the more customizable of the two, with a live editor, animated effects, custom cursors, a music player that plays full tracks, and video clips on the page, and removing branding costs €6.99 once against $24.99 for Lnk.Bio Unique. Lnk.Bio is the better toolkit for selling and marketing, with a shop, bookings, link scheduling, and ad pixels.',
  chooseMychar: [
    'want to design the page in a live editor, down to the cursor',
    'want full-length music and video clips that play on the page',
    'want live Twitch and YouTube sections and visitor comments',
    'want branding removed for €6.99 once'
  ],
  chooseThem: [
    'want a shop, bookings, or newsletter forms on your page',
    'want link scheduling, ad pixels, and UTM tools',
    'want hundreds of embeds and templates on the free plan'
  ],
  features: {
    free_plan: { yes: true, text: 'Unlimited links, with branding' },
    paid_plans: 'Mini $9.99, Unique $24.99 once, Ultra $89.99 a year',
    billing: 'One-time, monthly, or yearly',
    first_year: '$24.99 in total (Unique, removes branding)',
    custom_domain: { yes: true, text: '$39.99 a year add-on, or Ultra' },
    remove_branding: { yes: true, text: 'Unique, $24.99 once' },
    editor: 'Block list in a dashboard',
    drag_drop: { yes: true, text: 'Reorder blocks' },
    themes: { yes: true, text: '577 templates' },
    layouts: 'List, grid on Mini',
    fonts: 'Large web font library on Unique',
    effects: 'Animated themes on Mini',
    cursors: false,
    video_background: { yes: true, text: 'Unique, up to 50 MB' },
    custom_code: false,
    links: { yes: true, text: 'Unlimited, any URL' },
    music_player: { yes: true, text: 'Unique, trimmed to 1 minute' },
    video_clips: { yes: false, text: 'Video embeds' },
    comments: { yes: false, text: 'Reviews block' },
    view_counter: { yes: true, text: 'Mini' },
    qr_code: true,
    youtube: 'Embeds on every plan',
    twitch: 'Embeds on every plan',
    discord: { yes: true, text: 'Widget embed' },
    music_embeds: { yes: true, text: 'Spotify and more, every plan' },
    analytics_free: { yes: true, text: 'Views and link clicks' },
    analytics_history: 'Up to 2 years on Mini',
    store: { yes: true, text: 'Shop on Unique, 5% fee' },
    tips: 'PayPal donations on every plan',
    email_capture: { yes: true, text: 'Newsletter forms on Unique' },
    scheduling: { yes: true, text: 'Mini' },
    mobile_app: { yes: true, text: 'iOS and Android' }
  },
  plans: [
    { name: 'Free', price: '$0', period: 'forever', note: 'Unlimited links, 147 embeds, 577 templates, Lnk.Bio branding.' },
    { name: 'Mini', price: '$9.99', period: 'one-time', note: 'Or $0.99 monthly. Scheduling, grid layouts, advanced analytics, pixels.' },
    { name: 'Unique', price: '$24.99', period: 'one-time', note: 'No branding, custom colors and fonts, video wallpapers, Page Music, shop, forms.' },
    { name: 'Ultra', price: '$89.99', period: '/year', note: 'Adds verification, your own custom domain, bookings, and translations.' }
  ],
  plansNote: 'USD. Add-ons include a custom domain at $39.99 a year. Shop sales and paid bookings carry a 5% Lnk.Bio fee plus processor fees.',
  advantages: [
    {
      title: 'A live, on-page editor',
      description: 'Edit the page itself and see every change as you make it, instead of managing blocks in a list.',
      icon: 'i-lucide-mouse-pointer-click'
    },
    {
      title: 'Animated effects and cursors',
      description: 'Rain, snow, fire, sakura, plasma, and more, plus custom cursors and ten cursor trails on Elite. Lnk.Bio has animated themes but no cursors.',
      icon: 'i-lucide-sparkles'
    },
    {
      title: 'Full-length music',
      description: 'The music player plays your whole track, on every plan. Lnk.Bio Page Music is on Unique and trims tracks to one minute.',
      icon: 'i-lucide-music'
    },
    {
      title: 'Video clips that play in place',
      description: 'Upload up to 18 clips on Elite instead of embedding video from other sites.',
      icon: 'i-lucide-clapperboard'
    },
    {
      title: 'Cheaper branding removal',
      description: 'Pro removes branding for €6.99 once. On Lnk.Bio that takes Unique at $24.99 once.',
      icon: 'i-lucide-badge-euro'
    },
    {
      title: 'Live sections and comments',
      description: 'Twitch live status and schedule, your latest YouTube uploads, and a comment section visitors can post in.',
      icon: 'i-lucide-radio'
    }
  ],
  strengths: [
    {
      title: 'Built-in selling',
      description: 'Lnk.Bio Unique includes a shop for digital and physical products and services, and Ultra adds a booking calendar.'
    },
    {
      title: 'Marketing tools',
      description: 'Link scheduling, link groups, UTM presets, and ad pixels for Google, Meta, and TikTok start on the $9.99 Mini plan.'
    },
    {
      title: 'A big free plan',
      description: 'The free plan includes 147 embed types, including Spotify and Discord, and 577 templates.'
    },
    {
      title: 'Apps and long history',
      description: 'Lnk.Bio has run since 2016 and offers iOS and Android apps and browser extensions.'
    }
  ],
  sections: [
    {
      title: 'Why people look for a Lnk.Bio alternative',
      paragraphs: [
        'Lnk.Bio is a mature tool with fair, one-time pricing and a lot of features. Most of those features are about marketing and selling: scheduling, pixels, a shop, bookings, newsletter forms. People who move usually want the opposite emphasis, a page that looks like them, and find that design on Lnk.Bio means choosing from templates and, on Unique, colors, fonts, and wallpapers.',
        'Media is the other gap. Page Music trims your track to one minute, and video on the page comes from embeds. There are no custom cursors or particle-style effects.'
      ]
    },
    {
      title: 'How mychar.gg is different',
      paragraphs: [
        'mychar.gg spends its features on the page itself. The [editor](/docs/customization) is your page in edit mode, so you edit text in place, drag sections, links, and widgets into position, and pick layouts, fonts, colors, backgrounds, animated effects, and cursor trails while everything updates live. The music player plays full tracks on every plan, and Pro and Elite add video clips that play in place.',
        'Pricing is similar in spirit and lower in practice for design: Pro is €6.99 once and removes branding, and Elite is €12.99 once with every effect and custom font uploads. A custom domain is €2.99 per month on any plan. If you need a shop, bookings, or pixels, Lnk.Bio has them and mychar.gg does not.'
      ]
    }
  ],
  switchSteps: [
    { title: 'Claim your username', description: 'Sign up free at [mychar.gg/signup](/signup). Your page lives at mychar.gg/yourname.' },
    { title: 'Copy your links', description: 'Add your platforms from the built-in list, unlimited on every plan. Links to any other URL start on Pro.' },
    { title: 'Design it live', description: 'Pick a layout, fonts, and colors, set a background and an effect, and drag everything into order, or start from a community [skin](/skins).' },
    { title: 'Upload your media', description: 'Add full tracks to the music player and your own clips. The [clips guide](/docs/profile/clips) covers formats and limits.' },
    { title: 'Swap the link', description: 'Replace your lnk.bio link in every bio, or connect your own domain with the [custom domain add-on](/docs/profile/custom-domain).' }
  ],
  faq: [
    {
      label: 'Is mychar.gg cheaper than Lnk.Bio?',
      content: 'For a branding-free, designed page, yes. mychar.gg Pro is €6.99 once and Elite €12.99 once. Lnk.Bio removes branding on Unique at $24.99 once, and Ultra is $89.99 a year.'
    },
    {
      label: 'Can I add music like Page Music?',
      content: 'Yes, on every plan. The music player plays your full tracks: 1 on Free, 3 on Pro, and 6 on Elite. See the [audio guide](/docs/profile/audio).'
    },
    {
      label: 'Can I sell or take bookings on mychar.gg?',
      content: 'No. mychar.gg has no shop or booking calendar. You can link to them, but if selling on the page matters, Lnk.Bio fits better.'
    },
    {
      label: 'Can I use my own domain?',
      content: 'Yes. The custom domain add-on is €2.99 per month on any plan, including Free, and your mychar.gg link keeps working.'
    }
  ]
}

export default lnkbio
